import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { AddressContainerComponent } from "@app/address/components/address-dialog-container/address-dialog-container.component";
import { Address } from "@app/address/model/address";
import { EntityInfoTypes } from "@app/entity/model/entityinfo";
import { EntityInfoService } from "@app/entity/services/entityinfo.service";
import { Action } from "@ngrx/store";
import { BehaviorSubject, Observable, Subject, Subscription, filter, take, tap } from "rxjs";

@Component({
    selector: "clare-display-address",
    templateUrl: "./display-address.component.html",
    styleUrls: ["./display-address.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DisplayAddressComponent implements OnInit, OnDestroy {
    @Input() address: Address;
    @Input() titleDescriptor = "Address";
    @Input() addressType: "billingAddress" | "shippingAddress" | "projectAddress";
    @Input() includeLookup: boolean;
    @Input() customerInfoWidget = false;
    @Input() entityId: number;
    public builders$: Observable<any[]>;
    public builders: any[];
    public buildingTypes$: Observable<any[]>;
    public buildingTypes: any[];
    public communities: any[];
    public communities$: Observable<any[]>;

    /*
     * relayDialogEmitter
     * An emitter to used to relay updates from the inner component
     * up to the subscribing container component
     */
    @Output() relayDialogEmitter = new EventEmitter<{
        action: Action;
        closeDialog: EventEmitter<Action | void>;
    }>();
    /* */

    /*
     * closeDialog
     * An internal emitter subscribed to onInit which closes this.dialog when it emits
     * Should be passed up to and emitted by the container component
     */
    closeDialog = new EventEmitter<void>();
    /* */

    subscriptions: Subscription[] = [];
    unsubscribe$ = new Subject();
    private dialogRef: MatDialogRef<AddressContainerComponent>;

    constructor(private readonly dialog: MatDialog, private readonly entityInfoService: EntityInfoService) {}

    ngOnInit() {
        this.subscriptions.push(
            this.closeDialog.subscribe(() => {
                if (this.dialogRef) {
                    this.dialogRef.close();
                }
            })
        );

        if (this.entityId) {
            this.getEntityInfos(this.entityId);
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    edit(address: Address) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = true;
        this.dialogRef = this.dialog.open(AddressContainerComponent, dialogConfig);
        const instance = this.dialogRef.componentInstance;

        instance.includeLookup = this.includeLookup;
        this.relayInnerEmitter(instance.emitter);

        instance.address$ = new BehaviorSubject(address);
        instance.builders$ = this.builders$;
        instance.buildingTypes$ = this.buildingTypes$;
        instance.communities$ = this.communities$;
        instance.addressType = this.addressType;

        // Workaround for save button not enabling if includeLookup is false
        if (!this.includeLookup) {
            this.dialogRef.componentInstance.updateLookupStatus(false);
        }
    }

    relayInnerEmitter(emitter: EventEmitter<Action>) {
        const emitterSubscription = emitter.subscribe((action: Action) => {
            if (!action) {
                emitterSubscription.unsubscribe();
                this.closeDialog.emit();
            } else {
                this.relayDialogEmitter.emit({ action, closeDialog: this.closeDialog });
                emitterSubscription.unsubscribe();
            }
        });
    }

    private getEntityInfos(entityId) {
        this.entityInfoService.clearEntityInfoState();
        this.entityInfoService
            .selectEntityInfoListState(+entityId)
            .pipe(
                take(1),
                tap(_ => {
                    this.builders$ = this.entityInfoService.selectEntityInfoListByTypeFromStore(EntityInfoTypes.HomeBuilders).pipe(
                        take(1),
                        filter(builders => !!builders && !!builders[0])
                    );
                    this.communities$ = this.entityInfoService.selectEntityInfoListByTypeFromStore(EntityInfoTypes.HomeCommunities).pipe(
                        take(1),
                        filter(communities => !!communities && !!communities[0])
                    );

                    this.buildingTypes$ = this.entityInfoService.selectEntityInfoListByTypeFromStore(EntityInfoTypes.BuildingType).pipe(
                        take(1),
                        filter(buildingTypes => !!buildingTypes && !!buildingTypes[0])
                    );
                })
            )
            .subscribe();
    }
}
